import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react"
import { api, type CommentAttachment } from "../lib/api"
import { sizeProblem } from "../lib/fileTypes"

// The pieces every attachment surface shares: the comment editor's file
// picker, drop handling and chip list, and the comment display's card. A task
// description uses the same components, so how a file is picked, listed and
// removed is one implementation, not two that drift.
//
// Nothing here reads a body: whether an attachment is also painted inline in
// the markdown is the caller's filter (`attachmentsNotInBody` in
// lib/attachment-refs.ts).

export const MAX_FILES = 10

/** Uploads one file as an unclaimed draft. The id is what a comment or a
 *  description claims on submit; an abandoned draft is swept within a day. */
export async function uploadDraft(file: File) {
  const form = new FormData()
  form.append("file", file)
  return api.postMultipart<{ id: string; url: string }>("/projects/attachments", form)
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** The picker state a comment or a description editor holds: files chosen this
 *  session, existing attachments marked for removal, their previews and the
 *  drag state. One copy, so both editors cap, validate and upload the same
 *  way. */
export function useAttachmentSelection({
  initialAttachments = [],
  onError,
}: {
  initialAttachments?: CommentAttachment[]
  onError?: (message: string | null) => void
} = {}) {
  const [files, setFiles] = useState<File[]>([])
  const [removeIds, setRemoveIds] = useState<string[]>([])
  const [dragOver, setDragOver] = useState(false)

  // Picked files go up one request each, at submit. Sent inline with the
  // comment, ten of them shared one request body and its cap; a handful of
  // large files overran it before the route could say why. Kept across a
  // failed submit so a retry does not upload the same file twice.
  const uploaded = useRef(new Map<File, string>())

  // What the existing list still holds — a removed one frees its slot.
  const remaining = useMemo(
    () => initialAttachments.filter((a) => !removeIds.includes(a.id)),
    [initialAttachments, removeIds],
  )

  function addFiles(newFiles: File[]) {
    const combined = [...files, ...newFiles]
    if (remaining.length + combined.length > MAX_FILES) {
      onError?.(`Cannot attach more than ${MAX_FILES} files`)
      return
    }
    setFiles(combined)
    onError?.(null)
  }

  function removeFile(index: number) {
    setFiles(files.filter((_, i) => i !== index))
  }

  function toggleExisting(id: string) {
    setRemoveIds((current) =>
      current.includes(id) ? current.filter((rid) => rid !== id) : [...current, id],
    )
  }

  // One object URL per picked image, revoked when the selection changes or the
  // editor unmounts. Created inline in the JSX it minted a fresh URL on every
  // render and freed none of them.
  const previews = useMemo(
    () => files.map((file) => (file.type.startsWith("image/") ? URL.createObjectURL(file) : null)),
    [files],
  )
  useEffect(
    () => () => {
      for (const url of previews) if (url) URL.revokeObjectURL(url)
    },
    [previews],
  )

  /** Why the selection cannot be submitted, or null. Read at submit rather
   *  than on pick, so an oversized file is refused with the server's own
   *  message instead of never appearing at all. */
  function problem(): string | null {
    for (const file of files) {
      const why = sizeProblem(file)
      if (why) return why
    }
    if (remaining.length + files.length > MAX_FILES) {
      return `Cannot attach more than ${MAX_FILES} files`
    }
    return null
  }

  /** Uploads the picked files as drafts, once each, and returns their ids —
   *  what the claim on submit sends. */
  async function uploadPicked(): Promise<string[]> {
    const ids: string[] = []
    for (const file of files) {
      let id = uploaded.current.get(file)
      if (!id) {
        id = (await uploadDraft(file)).id
        uploaded.current.set(file, id)
      }
      ids.push(id)
    }
    return ids
  }

  function clear() {
    setFiles([])
    setRemoveIds([])
    uploaded.current.clear()
  }

  const dropHandlers = {
    onDragOver: (event: DragEvent) => {
      event.preventDefault()
      setDragOver(true)
    },
    onDragLeave: (event: DragEvent) => {
      event.preventDefault()
      setDragOver(false)
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault()
      setDragOver(false)
      addFiles(Array.from(event.dataTransfer.files))
    },
  }

  return {
    files,
    remaining,
    removeIds,
    dragOver,
    dropHandlers,
    previews,
    addFiles,
    removeFile,
    toggleExisting,
    problem,
    uploadPicked,
    clear,
  }
}

/** The editor's drop area: a file dropped on it is an attachment, not
 *  something to insert into the document, so the event is stopped here before
 *  the block editor sees it. */
export function AttachmentDropZone({
  dragOver,
  handlers,
  children,
}: {
  dragOver: boolean
  handlers: ReturnType<typeof useAttachmentSelection>["dropHandlers"]
  children: ReactNode
}) {
  return (
    <div
      className={`relative rounded-lg transition-colors ${dragOver ? "ring-2 ring-primary" : ""}`}
      {...handlers}
    >
      {children}

      {dragOver && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-primary/10 text-sm font-medium text-primary">
          Drop files here
        </div>
      )}
    </div>
  )
}

/** The chips under an editor: what the row already holds (restorable while
 *  marked) and what was picked this session (removable). */
export function AttachmentList({
  initialAttachments,
  removeIds,
  files,
  previews,
  onToggleExisting,
  onRemoveFile,
}: {
  initialAttachments: CommentAttachment[]
  removeIds: string[]
  files: File[]
  previews: (string | null)[]
  onToggleExisting: (id: string) => void
  onRemoveFile: (index: number) => void
}) {
  if (initialAttachments.length === 0 && files.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      {initialAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {initialAttachments.map((att) => {
            const isRemoved = removeIds.includes(att.id)
            return (
              <div
                key={att.id}
                className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${
                  isRemoved
                    ? "border-destructive/50 bg-destructive/10 opacity-50"
                    : "border-border bg-muted"
                }`}
              >
                {att.inline && att.mimeType.startsWith("image/") && att.url && (
                  <img
                    src={att.url}
                    alt={att.filename}
                    className="h-8 w-8 rounded object-cover"
                  />
                )}
                <span className="max-w-[150px] truncate">{att.filename}</span>
                <span className="text-muted-foreground">{formatFileSize(att.sizeBytes)}</span>
                <button
                  type="button"
                  onClick={() => onToggleExisting(att.id)}
                  className="text-muted-foreground hover:text-foreground"
                  title={isRemoved ? "Restore" : "Remove"}
                >
                  {isRemoved ? "↩" : "×"}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1 text-xs"
            >
              {previews[i] && (
                <img
                  src={previews[i]!}
                  alt={file.name}
                  className="h-8 w-8 rounded object-cover"
                />
              )}
              <span className="max-w-[150px] truncate">{file.name}</span>
              <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
              <button
                type="button"
                onClick={() => onRemoveFile(i)}
                className="text-muted-foreground hover:text-foreground"
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** The paperclip: a hidden multi-file input and the button that opens it. */
export function AttachmentPicker({
  onFiles,
  label = "Attach",
  title = "Attach files",
}: {
  onFiles: (files: File[]) => void
  label?: string
  title?: string
}) {
  const input = useRef<HTMLInputElement>(null)

  function select(event: React.ChangeEvent<HTMLInputElement>) {
    onFiles(Array.from(event.target.files || []))
    // Reset input so same file can be selected again
    if (input.current) input.current.value = ""
  }

  return (
    <>
      <input ref={input} type="file" multiple onChange={select} className="hidden" />
      <button
        type="button"
        onClick={() => input.current?.click()}
        className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        title={title}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
          />
        </svg>
        {label}
      </button>
    </>
  )
}

/** One attachment as a card under a body: thumbnail or file icon, filename
 *  link, size, and a delete control when the caller says so. */
export function AttachmentCard({
  attachment,
  canDelete,
  onDelete,
}: {
  attachment: CommentAttachment
  canDelete: boolean
  onDelete: () => void
}) {
  // Null when the server has no storage configured. The card still renders —
  // the row exists and saying so is more useful than a broken image — but
  // there is nothing to link to.
  const downloadUrl = attachment.url
  // Only a safe preview is painted as a thumbnail. An SVG is an image too, but
  // it is served as a download, so it gets the file icon like everything else.
  const isImage = attachment.inline && attachment.mimeType.startsWith("image/")

  return (
    <div className="group/att relative flex items-center gap-2 rounded-md border border-border bg-muted p-2">
      {isImage && downloadUrl ? (
        <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={downloadUrl}
            alt={attachment.filename}
            className="h-12 w-12 rounded object-cover hover:opacity-80"
          />
        </a>
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded bg-background">
          <svg
            className="h-6 w-6 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
      )}

      <div className="flex-1 min-w-0">
        {downloadUrl ? (
          <a
            href={downloadUrl}
            download={attachment.filename}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate text-sm font-medium hover:underline"
          >
            {attachment.filename}
          </a>
        ) : (
          <span
            className="block truncate text-sm font-medium text-muted-foreground"
            title="File storage is not configured on this server"
          >
            {attachment.filename}
          </span>
        )}
        <p className="text-xs text-muted-foreground">{formatFileSize(attachment.sizeBytes)}</p>
      </div>

      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute right-1 top-1 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover/att:opacity-100"
          title="Delete attachment"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
